import { useCallback, useState } from 'react';
import {
  Grid,
  Panel,
  Box,
  Button,
  OrganizationIcon,
  FilterButton,
  useTranslations,
  useSnackbar,
  usePopper,
  SearchableTextField,
  Tooltip,
  RenderIf,
  ISearchableTextFieldProps,
  IFilterButtonProps,
} from '@uniqkey-frontend/shared-app';
import { CreatePartnerOrganizationRequest } from '@uniqkey-backend-partner/api-client';
import ACLEnum from '../../enums/ACLEnum';
import { useUser } from '../../contexts/UserContext';
import usePartnerOrganizationsAPI from '../../hooks/usePartnerOrganizationsAPI';
import useOrganizationsTable from '../../hooks/tables/useOrganizationsTable';
import OrganizationsTable from '../../components/tables/OrganizationsTable';
import CreateOrganizationModal from './components/CreateOrganizationModal';
import OrganizationsListFilter, {
  IOrganizationsListFilterSubmitResult,
} from './components/OrganizationsListFilter';

const OrganizationsPage = () => {
  const { userCan } = useUser();

  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] = useState(false);
  const handleCreateOrganizationModalOpen = useCallback(
    () => setIsCreateOrganizationModalOpen(true),
    [],
  );
  const handleCreateOrganizationModalClose = useCallback(
    () => setIsCreateOrganizationModalOpen(false),
    [],
  );
  const [isCreateOrganizationLoading, setIsCreateOrganizationLoading] = useState(false);

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );

  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );

  const { t } = useTranslations();
  const { createOrganization } = usePartnerOrganizationsAPI();
  const { showSuccess, showError } = useSnackbar();

  const {
    selectedOrganizations,
    refetch,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    ...restTableProps
  } = useOrganizationsTable({
    noDataMessageKey: 'organizationsPage.noData',
  });

  const handleCreateOrganization = useCallback(async (
    organization: CreatePartnerOrganizationRequest,
  ) => {
    try {
      setIsCreateOrganizationLoading(true);
      await createOrganization(organization);
      showSuccess({
        text: t('createOrganizationModal.organizationCreated'),
      });
      handleCreateOrganizationModalClose();
      refetch();
    } catch (error) {
      showError({ text: t('common.somethingWentWrong') });
    } finally {
      setIsCreateOrganizationLoading(false);
    }
  }, [createOrganization, handleCreateOrganizationModalClose, refetch, showError, showSuccess, t]);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((updatedValues: IOrganizationsListFilterSubmitResult) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  return (
    <Grid container flexDirection="column" className="min-height-100-percent">
      <Grid item mb={1}>
        <Panel>
          <Box p={1}>
            <Grid container justifyContent="space-between" alignItems="stretch">
              <Grid item xs={3} container flexWrap="nowrap">
                <Grid item>
                  <Tooltip title={t('common.filter')}>
                    <FilterButton
                      isFilterActive={isFilterActive}
                      numberOfActiveFilters={numberOfActiveFilters}
                      selected={isFilterOpen}
                      onChange={toggleIsFilterOpen}
                    />
                  </Tooltip>
                </Grid>
                {/* TODO: actions here */}
              </Grid>
              <Grid item xs={9} container justifyContent="flex-end" flexWrap="nowrap">
                <Grid item>
                  <SearchableTextField
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={t('common.search')}
                  />
                </Grid>
                <RenderIf condition={userCan(ACLEnum.OrganizationCreate)}>
                  <Box mr={3} />
                  <Grid item>
                    <Button
                      icon={<OrganizationIcon />}
                      onClick={handleCreateOrganizationModalOpen}
                    >
                      {t('organizationsPage.createOrganizationButton')}
                    </Button>
                  </Grid>
                </RenderIf>
              </Grid>
            </Grid>
          </Box>
        </Panel>
      </Grid>
      <Grid item xs>
        <Panel>
          <OrganizationsTable
            selectedOrganizations={selectedOrganizations}
            {...restTableProps}
          />
        </Panel>
      </Grid>
      {isCreateOrganizationModalOpen && (
        <CreateOrganizationModal
          isOpen={isCreateOrganizationModalOpen}
          isLoading={isCreateOrganizationLoading}
          onSubmit={handleCreateOrganization}
          onClose={handleCreateOrganizationModalClose}
        />
      )}
      <OrganizationsListFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
    </Grid>
  );
};

export default OrganizationsPage;
